import React from 'react';
import cx from 'classnames';
import {kebabCase} from 'lodash';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import {useAnalytics} from '../hooks';
import {isAbsoluteURL, getNodeText} from '../util';
import Link from './Link';
import {SyntaxHighlighter} from './SyntaxHighlighter';
import {LinkIcon} from './svg';
import CalloutBox from './Callout';

export const HeaderWithAnchor = ({children, className, as: Component = 'h1'}) => {
  const id = kebabCase(getNodeText(children));

  const onClick = (e) => {
    // Updates hash without scrolling, which is handled by parent
    e.preventDefault();
    window.location = e.currentTarget.href;
  };

  return (
    <Component id={id} className={cx('group', className)}>
      {children}
      <a
        className="ml-2 text-base opacity-0 text-gray-600 hover:opacity-100 group-hover:opacity-75 no-underline"
        href={`#${id}`}
        onClick={onClick}
        aria-label="Anchor link"
      >
        <LinkIcon className="-mt-1" />
      </a>
    </Component>
  );
};

export const H1 = (props) => <HeaderWithAnchor as="h1" {...props} />;
export const H2 = (props) => <HeaderWithAnchor as="h2" {...props} />;

const Markdown = (props) => (
  <ReactMarkdown
    plugins={[gfm]}
    rehypePlugins={props.allowRaw ? [rehypeRaw] : undefined}
    components={{
      h1: H1,
      h2: H2,
      code: SyntaxHighlighter,
      CalloutBox,
    }}
    unwrapDisallowed
    {...props}
  />
);

export const CommentMarkdown = (props) => (
  <Markdown
    disallowedElements={[
      'a',
      'html',
      'image',
      'imageReference',
      'link',
      'linkReference',
      'parsedHtml',
      'pre',
      'virtualHtml',
    ]}
    components={{
      h1: ({children}) => <h2 className="my-2">{children}</h2>,
      h2: ({children}) => <h2 className="my-2">{children}</h2>,
      code: SyntaxHighlighter,
    }}
    {...props}
  />
);

// forceAnchor is primarily for protecting against CMS content
// with stale links that can break client side navigation
export const A = ({href, children}) => {
  const {tracker} = useAnalytics();

  const onClick = () => {
    tracker.lessonClickLink({
      children,
      href,
      absoluteURL: isAbsoluteURL(href),
    });
  };

  return (
    <Link
      href={href}
      onClick={onClick}
      forceAnchor
    >
      {children}
    </Link>
  );
};

export const Table = ({children, className, ...props}) => (
  <table className={cx('not-prose min-w-full divide-y divide-gray-300 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg', className)} {...props}>
    {children}
  </table>
);

export const THead = ({children, className, ...props}) => (
  <thead className={cx('bg-gray-50', className)} {...props}>
    {children}
  </thead>
);

export const TBody = ({children, className, ...props}) => (
  <tbody className={cx('divide-y divide-gray-200 bg-white', className)} {...props}>
    {children}
  </tbody>
);

export const TH = ({children, className, ...props}) => (
  <th className={cx('px-3 py-3.5 text-left text-sm font-semibold text-gray-900', className)} {...props}>
    {children}
  </th>
);

export const TD = ({children, className, ...props}) => (
  <td className={cx('px-3 py-4 text-sm text-gray-500', className)} {...props}>
    {children}
  </td>
);

export const LI = ({children, className, ...props}) => (
  <li className={cx('mt-0 mb-0', className)} {...props}>
    {children}
  </li>
);

export default Markdown;
